import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import type { RootState } from "../store";

interface QlikVariable {
  name: string;
  object: any;
}

interface QlikCalcVariable {
  [key: string]: any;
}

interface QlikCalcVariables {
  [calcVarName: string]: QlikCalcVariable;
}

interface QlikState {
  qlikVariables: QlikVariable[];
  qlikCalcVariables: QlikCalcVariables;
  variableReportFor?: string;
  variableReportBy?: string;
}

const initialState: QlikState = {
  qlikVariables: [],
  qlikCalcVariables: {},
  variableReportFor: "",
  variableReportBy: "",
};

const qlikVariableSlice = createSlice({
  name: "qlikVariable",
  initialState,
  reducers: {
    // Mutation to add a Qlik object
    setQlikVariable(state, action: PayloadAction<{ appName: string, name: string; object: any }>) {
      // state.qlikObjects.push(action.payload);
      const idx = state.qlikVariables.findIndex(qlik => qlik.name === action.payload.name);
        if (idx !== -1) {
          // Replace existing object
          state.qlikVariables[idx] = action.payload;
        } else {
          // Add new object
          state.qlikVariables.push(action.payload);
        }
    },
    setQlikCalcVariables(state, action: PayloadAction<{ varObjName: string; variableName: string; variableValue: any }>) {
      const { varObjName, variableName, variableValue } = action.payload;
      
      // Initialize the calcVar object if it doesn't exist
      if (!state.qlikCalcVariables[varObjName]) {
        state.qlikCalcVariables[varObjName] = {};
      }
      
      // Update the specific variable within the calcVar object
      state.qlikCalcVariables[varObjName] = {
        ...state.qlikCalcVariables[varObjName],
        [variableName]: variableValue,
      };
    },
    initQlikCalcVariables(state, action: PayloadAction<{ varObjName: string; varObject: QlikCalcVariable }>) {
      const { varObjName, varObject } = action.payload;
      state.qlikCalcVariables[varObjName] = varObject;
    },
    setVariableReportFor(state, action: PayloadAction<string>) {
      state.variableReportFor = action.payload;
    },
    setVariableReportBy(state, action: PayloadAction<string>) {
      state.variableReportBy = action.payload;
    },
  },
});

// Actions
export const { 
  setQlikVariable,
  setQlikCalcVariables,
  initQlikCalcVariables,
  setVariableReportFor,
  setVariableReportBy
} = qlikVariableSlice.actions;

// Reducer
export default qlikVariableSlice.reducer;

// Selectors
export const selectQlikVariables = (state: RootState) => state.qlikVariable.qlikVariables;
export const selectQlikCalcVariables = (state: RootState) => state.qlikVariable.qlikCalcVariables;

// Memoized selector to get a specific calcVar object by name
export const selectQlikCalcVariableByName = createSelector(
  [selectQlikCalcVariables, (_: RootState, calcVarName: string) => calcVarName],
  (qlikCalcVariables, calcVarName) => qlikCalcVariables[calcVarName] ?? {}
);

// Memoized selector to get a Qlik variable object by name
export const selectQlikVariableByName = createSelector(
  [selectQlikVariables, (_: RootState, name: string) => name],
  (qlikVariables, name) => qlikVariables.find((qlik:any) => qlik.name === name)?.object ?? null
);

export type { QlikState, QlikVariable, QlikCalcVariable, QlikCalcVariables };